import { BetResponseCode } from '@pascal/pascal-core';
import {
  divide as divideNumbers,
  multiply,
  plus as plusNumbers,
  toFixed,
} from './arithmeticOperations';
import { additionalBetAmountChangingButtons } from '../enums';
import { doBetStore } from '../store';
import { maxRangeValue } from './constants';

export const decidedCurrentValue = (
  condition,
  value,
  finishedValue,
  showNotification,
  responseCode
) => {
  const currentValue = condition ? value : finishedValue;
  if (currentValue === finishedValue && showNotification && responseCode) {
    showNotification({ responseCode });
  }
  return condition ? value : finishedValue;
};

export const changeBetAmount = (
  functionName,
  callback,
  limits,
  balance,
  amount,
  showNotification
) => {
  const { plus, min, allIn, double, divide, max, minus } = additionalBetAmountChangingButtons;
  const { InValidMaxBet, InValidMinBet, InsufficientFunds } = BetResponseCode;
  const { minBet, maxBet } = limits;

  const functionsForChangBetAmount = {
    [plus]: () =>
      callback(
        decidedCurrentValue(
          amount !== maxBet && amount < maxBet && plusNumbers(amount, minBet) <= maxBet,
          toFixed(plusNumbers(amount, minBet), 2),
          maxBet,
          amount + Number(minBet) > maxBet && showNotification,
          InValidMaxBet
        )
      ),
    [minus]: () =>
      callback(
        decidedCurrentValue(
          amount >= minBet && amount !== minBet && amount - Number(minBet) >= minBet,
          toFixed(amount - Number(minBet), 2),
          minBet,
          amount - Number(minBet) < minBet && showNotification,
          InValidMinBet
        )
      ),
    [min]: () => {
      if (balance < minBet) {
        showNotification({ responseCode: InsufficientFunds });
      }
      doBetStore.setBetAmount(minBet);
    },
    [max]: () => callback(decidedCurrentValue(balance <= maxBet, toFixed(balance, 2), maxBet)),
    [divide]: () =>
      callback(
        decidedCurrentValue(
          amount > minBet && divideNumbers(amount, 2) >= minBet,
          toFixed(divideNumbers(amount, 2), 2),
          minBet,
          amount / 2 < minBet && showNotification,
          InValidMinBet
        )
      ),
    [double]: () =>
      callback(
        decidedCurrentValue(
          amount <= maxBet && amount !== maxBet && multiply(amount, 2) < maxBet,
          toFixed(multiply(amount, 2), 2),
          maxBet,
          amount * 2 > maxBet && showNotification,
          InValidMaxBet
        )
      ),
    [allIn]: () => {
      if (balance > maxBet) {
        showNotification({ responseCode: InValidMaxBet });
      } else if (balance && balance < minBet) {
        showNotification({ responseCode: InValidMinBet });
      } else if (!balance) {
        showNotification({ responseCode: InValidMinBet });
      }
      if (amount !== maxBet) {
        callback(balance > maxBet ? maxBet : !balance ? minBet : toFixed(balance, 2));
      }
    },
  };

  functionsForChangBetAmount?.[functionName]();
};

export const checkDoBetModel = (limits, userBalance, amount) => {
  let generatedBetResponseCode;
  if (Number(amount) < limits.minBet) {
    generatedBetResponseCode = BetResponseCode.InValidMinBet;
  } else if (Number(amount) > limits.maxBet) {
    generatedBetResponseCode = BetResponseCode.InValidMaxBet;
  }
  return generatedBetResponseCode;
};

export const betAmountValidator = (betAmount, limits) => {
  let validatedBetAmount = null;
  let errorMessageCode = null;
  const { maxBet, minBet } = limits;

  switch (true) {
    case !maxBet || (betAmount > maxBet && maxBet < minBet): {
      errorMessageCode = BetResponseCode.InsufficientFunds;
      validatedBetAmount = minBet;
      break;
    }
    case Number.isNaN(betAmount) || betAmount <= minBet: {
      if (betAmount < minBet) {
        errorMessageCode = BetResponseCode.InValidMinBet;
      }
      validatedBetAmount = minBet;
      break;
    }
    case betAmount > maxBet: {
      errorMessageCode = BetResponseCode.InValidMaxBet;

      validatedBetAmount = maxBet;
      break;
    }
    default:
      validatedBetAmount = betAmount;
  }

  return { validatedBetAmount, errorMessageCode };
};

export const checkValidRange = (isUnder, rangeValue, margin = 4, minValue = 1, maxValue = 98) => {
  let value;

  if (isUnder) {
    if (rangeValue + margin >= maxRangeValue) {
      value = maxRangeValue - margin;
    } else if (rangeValue <= minValue) {
      value = minValue;
    } else {
      value = rangeValue;
    }
  } else if (rangeValue <= margin) {
    value = margin;
  } else if (rangeValue >= maxValue) {
    value = maxValue;
  } else {
    value = rangeValue;
  }
  return value;
};
